import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { TextArea } from '../../../components/ui/TextArea';
import { AvatarUpload } from '../../../components/ui/AvatarUpload';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { MAX_CARDS_PER_USER, RESERVED_SLUGS } from '../../../lib/constants';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const CreateCard = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    internal_name: '', public_slug: '', full_name: '', job_title: '',
    company_name: '', email: '', phone: '', website: '',
    location: '', bio: '', profile_image_url: '', template_id: 'modern'
  });

  useEffect(() => {
    const checkLimits = async () => {
      try {
        const { count, error } = await supabase
          .from('cards')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .neq('status', 'archived');
        if (error) throw error;
        if (count >= MAX_CARDS_PER_USER) {
          setError(`You’ve reached the current five-card limit for your Meetoi account.`);
        }
      } catch (err) {
        setError('Failed to verify account limits.');
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) checkLimits();
  }, [user]);

  const generateSlug = (name) => {
    if (!name) return '';
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  };

  const handleChoice = (type) => {
    if (type === 'profile' && profile) {
      setFormData(prev => ({
        ...prev,
        internal_name: `${profile.full_name || 'My'}'s Card`,
        public_slug: generateSlug(profile.full_name || ''),
        full_name: profile.full_name || '',
        job_title: profile.job_title || '',
        company_name: profile.company_name || '',
        email: user.email || '',
        phone: profile.phone || '',
        website: profile.website || '',
        location: profile.location || '',
        bio: profile.bio || '',
        profile_image_url: profile.avatar_url || ''
      }));
    } else {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
    setStep(2);
  };

  const validateAndSuggestSlug = async (baseSlug) => {
    const cleanSlug = generateSlug(baseSlug);
    if (cleanSlug.length < 3) return { valid: false, error: 'Slug must be at least 3 characters long.' };
    if (RESERVED_SLUGS.includes(cleanSlug)) return { valid: false, error: 'This slug is reserved. Please choose another.' };
    
    let currentSlug = cleanSlug;
    let counter = 2;
    while (true) {
      const { data } = await supabase.from('cards').select('id').eq('public_slug', currentSlug);
      if (!data || data.length === 0) break;
      currentSlug = `${cleanSlug}-${counter}`;
      counter++;
    }
    
    if (currentSlug !== cleanSlug) {
      return { valid: false, suggested: currentSlug, error: `The slug "${cleanSlug}" is already taken. We've suggested "${currentSlug}".` };
    }
    return { valid: true, slug: currentSlug };
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!formData.internal_name.trim()) return setError("Internal card name is required.");
    if (!formData.full_name.trim()) return setError("Public full name is required.");
    if (!formData.job_title.trim()) return setError("Job title is required.");
    if (!formData.public_slug.trim()) return setError("Public slug is required.");

    setSaving(true);
    try {
      const slugCheck = await validateAndSuggestSlug(formData.public_slug);
      if (!slugCheck.valid) {
        if (slugCheck.suggested) setFormData(prev => ({ ...prev, public_slug: slugCheck.suggested }));
        throw new Error(slugCheck.error);
      }
      setFormData(prev => ({ ...prev, public_slug: slugCheck.slug }));
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFinalSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data, error: insertError } = await supabase
        .from('cards')
        .insert([{
          user_id: user.id,
          internal_name: formData.internal_name,
          public_slug: formData.public_slug,
          full_name: formData.full_name,
          job_title: formData.job_title,
          company_name: formData.company_name,
          email: formData.email,
          phone: formData.phone,
          website: formData.website,
          location: formData.location,
          bio: formData.bio,
          profile_image_url: formData.profile_image_url,
          template_id: formData.template_id,
          status: 'draft'
        }])
        .select()
        .single();
      if (insertError) throw insertError;
      navigate(`/app/cards/${data.id}/edit`);
    } catch (err) {
      setError(err.message || 'An error occurred creating your card.');
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  if (error && step === 1) {
    return (
      <div className="max-w-2xl mx-auto pt-12">
        <Alert variant="warning" title="Limit Reached" message={error} />
        <Button className="mt-6" onClick={() => navigate('/app/cards')}>Return to Cards</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-16">
      <div className="mb-8 flex items-center justify-center space-x-2">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`h-2 w-16 rounded-full transition-colors duration-300 ${s <= step ? 'bg-slate-900' : 'bg-slate-200'}`} />
        ))}
      </div>

      <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-200 shadow-sm">
        {error && <Alert variant="error" message={error} className="mb-8" />}

        {step === 1 && (
          <div className="animate-in fade-in duration-500 text-center">
            <h1 className="text-3xl font-extrabold text-slate-900 mb-4">Create your digital card</h1>
            <p className="text-lg text-slate-600 mb-10">Start with your professional profile or build a card from scratch.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <button onClick={() => handleChoice('profile')} className="flex flex-col items-center p-8 border-2 border-slate-200 rounded-2xl hover:border-slate-900 hover:bg-slate-50 transition-all text-left group">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-white transition-colors">
                  <SafeIcon icon={FiIcons.FiUser} className="text-2xl text-slate-600 group-hover:text-slate-900" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Use my profile</h3>
                <p className="text-sm text-slate-500 text-center">Copies your existing professional details as a starting point.</p>
              </button>
              <button onClick={() => handleChoice('blank')} className="flex flex-col items-center p-8 border-2 border-slate-200 rounded-2xl hover:border-slate-900 hover:bg-slate-50 transition-all text-left group">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-white transition-colors">
                  <SafeIcon icon={FiIcons.FiFileText} className="text-2xl text-slate-600 group-hover:text-slate-900" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Start blank</h3>
                <p className="text-sm text-slate-500 text-center">Creates an empty card for a completely new identity.</p>
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-in fade-in duration-500">
            <header className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900">Basic card details</h2>
              <p className="text-slate-600 mt-2">These details are specific to this card and won't change your main profile.</p>
            </header>

            <form onSubmit={handleStep2Submit} className="space-y-8">
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 space-y-6">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Internal Setup</h3>
                <Input 
                  label={<>Internal Card Name <span className="text-red-500">*</span></>} 
                  placeholder="e.g. Sales Card, Startup Pitch" 
                  value={formData.internal_name} 
                  onChange={(e) => setFormData({ ...formData, internal_name: e.target.value })} 
                  required 
                />
                <div>
                  <Input 
                    label={<>Public Slug <span className="text-red-500">*</span></>} 
                    placeholder="e.g. jane-doe" 
                    value={formData.public_slug} 
                    onChange={(e) => setFormData({ ...formData, public_slug: e.target.value.toLowerCase() })} 
                    required 
                  />
                  <p className="text-xs text-slate-500 mt-2 font-mono bg-white p-2 rounded border border-slate-200 inline-block">
                    meetoi.com/c/<span className="text-slate-900 font-bold">{formData.public_slug || 'slug'}</span>
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2">Public Identity</h3>
                <AvatarUpload url={formData.profile_image_url} onUpload={(url) => setFormData({ ...formData, profile_image_url: url })} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input label={<>Full Name <span className="text-red-500">*</span></>} value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} required />
                  <Input label={<>Job Title <span className="text-red-500">*</span></>} value={formData.job_title} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} required />
                  <Input label="Company (Optional)" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} />
                  <Input label="Location (Optional)" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
                </div>
                <TextArea label="Short Bio (Optional)" value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} maxLength={500} rows={3} />
              </div>

              <div className="space-y-6">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2">Contact Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input label="Email (Optional)" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  <Input label="Phone (Optional)" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  <Input label="Website (Optional)" type="url" className="md:col-span-2" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                </div>
              </div>

              <div className="pt-6 flex justify-between border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setStep(1)} disabled={saving}>Back</Button>
                <Button type="submit" isLoading={saving}>Continue to Style</Button>
              </div>
            </form>
          </div>
        )}

        {step === 3 && (
          <div className="animate-in fade-in duration-500">
            <header className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900">Choose a starting style</h2>
              <p className="text-slate-600 mt-2">Select the initial layout for your card. You can change this later.</p>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
              {[
                { id: 'executive', name: 'Executive', desc: 'Premium, restrained and corporate.' },
                { id: 'modern', name: 'Modern', desc: 'Clean, confident and contemporary.' },
                { id: 'bold', name: 'Bold', desc: 'Stronger visual accents with startup energy.' }
              ].map(template => (
                <div key={template.id} onClick={() => setFormData({ ...formData, template_id: template.id })} className={`cursor-pointer rounded-2xl border-2 p-6 transition-all ${formData.template_id === template.id ? 'border-slate-900 bg-slate-50 shadow-sm' : 'border-slate-200 hover:border-slate-400'}`}>
                  <div className="w-full h-32 bg-white border border-slate-200 rounded-lg mb-4 flex flex-col p-3 gap-2 shadow-sm">
                    <div className="flex gap-2">
                      <div className={`w-8 h-8 rounded-full bg-slate-200 ${template.id === 'bold' ? 'bg-indigo-200' : ''}`} />
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-2 bg-slate-200 rounded w-3/4" />
                        <div className="h-2 bg-slate-100 rounded w-1/2" />
                      </div>
                    </div>
                    {template.id === 'executive' && <div className="h-0.5 bg-slate-100 w-full my-1" />}
                    <div className="flex-1 rounded bg-slate-50 border border-slate-100 mt-auto" />
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-slate-900">{template.name}</h3>
                    {formData.template_id === template.id && <SafeIcon icon={FiIcons.FiCheck} className="text-slate-900" />}
                  </div>
                  <p className="text-xs text-slate-500">{template.desc}</p>
                </div>
              ))}
            </div>
            <div className="pt-6 flex justify-between border-t border-slate-100">
              <Button type="button" variant="ghost" onClick={() => setStep(2)} disabled={saving}>Back</Button>
              <Button onClick={handleFinalSubmit} isLoading={saving} size="lg">Create Card</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};